/* istanbul ignore file */

import type { FormEvent } from 'react';
import { useState } from 'react';
import { useMap } from 'react-use';
import type { Dictionary } from '@noths/polaris-dev-ts-recipes';
import * as R from 'ramda';

import type {
  AsFormValue,
  DeregisterValidationFn,
  FormBlurEventFn,
  FormChangeEventFn,
  FormOptions,
  HasErrorFn,
  HasValidationFn,
  RegisterValidationFn,
  SetFormValueFn,
  Target,
} from './types/form';
import type {
  FormValidationResult,
  ValidationErrors,
  ValidationRules,
  Validations,
} from './types/validation';
import {
  getDeeplyUpdatedState,
  getFieldNameAsList,
  getFieldValidation,
  hasNestedPath,
  isCheckboxInput,
} from './utils';

export const useForm = <
  TState extends Dictionary<TState>,
  TValidations extends Validations<Partial<TState>, TState> = Validations<Partial<TState>, TState>,
>(
  initialState: TState,
  validationsMap = {} as TValidations,
  options?: FormOptions,
) => {
  const [canValidate, setCanValidate] = useState(options?.validateOnChange ?? false);
  const [errors, { remove: clearError, set: setError }] = useMap<ValidationErrors<TValidations>>(
    {},
  );
  const [formValues, { reset: resetForm, setAll: setFormValues }] = useMap<TState>(initialState);
  const [validations, { remove: removeValidation, set: setValidation }] =
    useMap<TValidations>(validationsMap);

  const setFormValue: SetFormValueFn<TState> = (name, value) => {
    if (hasNestedPath(name as string)) {
      const updatePathList = getFieldNameAsList(name as string);
      const updatedState = getDeeplyUpdatedState(updatePathList, value, formValues);

      setFormValues(updatedState);

      if (canValidate) {
        validateInput(name, value, updatedState);
      }

      return;
    }

    const updatedState = {
      ...formValues,
      [name]: value,
    };

    setFormValues(updatedState);

    if (canValidate) {
      validateInput(name, value, updatedState);
    }
  };

  const setFormError = (key: string | keyof TState, message: string | boolean) => {
    setError(key as keyof TState, message);
  };

  const hasFormError: HasErrorFn = (key) => errors[key as keyof TState] != null;

  const clearFormError = (key: string | keyof TState) => {
    clearError(key as keyof TState);
  };

  const hasValidation: HasValidationFn = (name) => validations[name] != null;

  const registerValidation: RegisterValidationFn<TState> = (name, validation) => {
    setValidation(name as keyof TValidations, validation as TValidations[keyof TValidations]);
  };

  const deregisterValidation: DeregisterValidationFn = (key) => {
    removeValidation(key as keyof TValidations);

    if (errors[key]) {
      clearFormError(key);
    }
  };

  const validateInput = <TField extends keyof TState>(
    field: TField,
    value: TState[TField],
    state?: TState,
  ) => {
    const fieldRules = validations[field] || {};

    const validationResult = getFieldValidation(
      fieldRules as ValidationRules<TState>,
      value,
      state || formValues,
    );

    if (validationResult.isInvalid) {
      setFormError(field, validationResult.error || true);

      return true;
    }

    if (errors[field]) {
      clearFormError(field);
    }

    return false;
  };

  const handleBlur: FormBlurEventFn = (event) => {
    const { name } = event.target as Target<TState>;

    const fieldValuePath = getFieldNameAsList(name);
    const fieldValue = R.path<AsFormValue<TState>>(fieldValuePath, formValues);

    if (canValidate) {
      validateInput(name, fieldValue as AsFormValue<TState>);
    }
  };

  const handleChange: FormChangeEventFn = (event) => {
    const { checked, name, value } = event.target as Target<TState>;
    const updateValue = isCheckboxInput(event) ? checked : value;

    if (hasNestedPath(name)) {
      const updatePathList = getFieldNameAsList(name as string);
      const updatedState = getDeeplyUpdatedState(updatePathList, updateValue, formValues);

      setFormValues(updatedState);

      if (canValidate) {
        validateInput(name, updateValue, updatedState);
      }

      return;
    }

    const updatedState = {
      ...formValues,
      [name]: updateValue,
    };

    setFormValues(updatedState);

    if (canValidate) {
      validateInput(name, updateValue, updatedState);
    }
  };

  const getFormValidation = (
    subsectionValidationRuleNames?: string[],
  ): FormValidationResult<TValidations> => {
    const validationRuleNames = subsectionValidationRuleNames
      ? subsectionValidationRuleNames
      : Object.keys(validations);

    const validationErrors = validationRuleNames.reduce((validations, field) => {
      const fieldValuePath = getFieldNameAsList(field);
      const fieldValue = R.path<AsFormValue<TState>>(fieldValuePath, formValues)!;

      const isInvalidInput = validateInput(field as keyof TState, fieldValue);

      if (isInvalidInput) {
        return {
          ...validations,
          [field]: true,
        };
      }

      return validations;
    }, {} as ValidationErrors<TValidations>);

    return {
      isValid: Object.keys(validationErrors).length === 0,
      validationErrors,
    };
  };

  const handleSubmit = (
    e: FormEvent,
    submitCallback: VoidFunction,
    validationFailureCallback?: (validationErrors: typeof errors) => void,
  ) => {
    e.preventDefault();

    if (options?.validateOnChangeAfterSubmit) {
      setCanValidate(true);
    }

    const validationResult = getFormValidation();
    const isValidForm = !Object.keys(validations).length || validationResult.isValid;

    if (isValidForm) {
      submitCallback();

      if (options?.resetFormOnSubmit) {
        resetForm();
      }
      return;
    }

    if (validationFailureCallback) {
      validationFailureCallback(validationResult.validationErrors);
    }
  };

  return {
    errors,
    formValues,
    clearFormError,
    deregisterValidation,
    getFormValidation,
    handleBlur,
    handleChange,
    handleSubmit,
    hasFormError,
    hasValidation,
    registerValidation,
    resetForm,
    setFormError,
    setFormValue,
    setFormValues,
  };
};
